/**
 * Выбор нескольких ИП. В адресе — повторы ключа `entrepreneur_id=1&entrepreneur_id=3`
 * (так ручки принимают список); пусто — все ИП.
 *
 * Спека: `docs/specs/2026-09-30-payroll-breakdown-design.md`, п. 1.
 */
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { DirectoryRow } from "@/lib/statements";

export const ENTREPRENEUR_KEY = "entrepreneur_id";

/** Выбранные ИП из адреса. */
export function entrepreneurIds(params: URLSearchParams): string[] {
  return params.getAll(ENTREPRENEUR_KEY).filter(Boolean);
}

/** Копия адреса с другим выбором ИП. */
export function withEntrepreneurs(params: URLSearchParams, ids: readonly string[]): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(ENTREPRENEUR_KEY);
  for (const id of ids) next.append(ENTREPRENEUR_KEY, id);
  return next;
}

export function EntrepreneurPicker(props: {
  entrepreneurs: DirectoryRow[];
  value: readonly string[];
  onChange: (ids: string[]) => void;
}) {
  const { entrepreneurs, value } = props;
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const chosen = entrepreneurs.filter((e) => value.includes(String(e.id)));
  const label =
    value.length === 0
      ? "Все ИП"
      : chosen.length === 1
        ? (chosen[0]?.name ?? "1 ИП")
        : `ИП: ${value.length}`;

  const toggle = (id: string) =>
    props.onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={chosen.map((e) => e.name).join(", ") || "Все ИП"}
        className="flex h-8 max-w-[260px] items-center gap-1.5 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] outline-none hover:border-border-strong"
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={13} className="flex-none text-fg-faint" />
      </button>
      {open ? (
        <div className="absolute left-0 top-9 z-40 min-w-[240px] rounded-[var(--radius-card)] border border-border bg-bg py-1 text-[12.5px] shadow-lg">
          <button
            type="button"
            className={`block w-full px-3 py-1.5 text-left hover:bg-bg-hover ${value.length ? "" : "font-medium"}`}
            onClick={() => props.onChange([])}
          >
            Все ИП
          </button>
          {entrepreneurs.map((e) => (
            <label key={e.id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-bg-hover">
              <input type="checkbox" checked={value.includes(String(e.id))} onChange={() => toggle(String(e.id))} />
              <span className="whitespace-nowrap">{e.name}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}
